import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CommentMediaProps {
  path: string;
  type: "image" | "video" | null;
}

/**
 * Resolves a short-lived signed URL for a comment attachment. The
 * `comment-media` bucket is private; storage RLS only exposes files whose
 * corresponding comment is approved (or belongs to the current user/admin).
 * Legacy rows that stored a full public URL are rendered as-is so they don't
 * break, but new uploads always store the storage path.
 */
export default function CommentMedia({ path, type }: CommentMediaProps) {
  const [url, setUrl] = useState<string | null>(null);
  const isLegacyUrl = /^https?:\/\//i.test(path);

  useEffect(() => {
    if (isLegacyUrl) {
      setUrl(path);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage
        .from("comment-media")
        .createSignedUrl(path, 60 * 60);
      if (!cancelled) setUrl(data?.signedUrl ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [path, isLegacyUrl]);

  if (!url) return null;

  if (type === "image") {
    return (
      <img
        src={url}
        alt="Comment attachment"
        className="max-h-80 rounded-lg cursor-pointer"
        onClick={() => window.open(url, "_blank")}
      />
    );
  }
  return <video src={url} controls className="max-h-80 rounded-lg w-full max-w-lg" />;
}
